/**
 * Structured Data Mixin
 * 
 * This mixin provides methods to inject JSON-LD structured data (Schema.org markup)
 * into the page head for SEO purposes.
 * 
 * Usage in components:
 * 
 * import structuredDataMixin from '@/mixins/structuredData';
 * 
 * export default {
 *   mixins: [structuredDataMixin],
 *   mounted() {
 *     this.setStructuredData(this.getLocalBusinessSchema('Gold Coast'));
 *     this.setStructuredData(this.getFAQSchema(this.faqItems));
 *   }
 * }
 */

export default {
  methods: {
    /**
     * Injects structured data script tag into the page head
     * @param {Object} schema - The Schema.org JSON-LD object
     */
    setStructuredData(schema) {
      // Remove any existing structured data script tags for this schema type
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-schema-type]');
      const schemaType = schema['@type'];

      existingScripts.forEach(script => {
        if (script.getAttribute('data-schema-type') === schemaType) {
          script.remove();
        }
      });

      // Create new script tag
      const scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.setAttribute('data-schema-type', schemaType);
      scriptTag.text = JSON.stringify(schema);

      document.head.appendChild(scriptTag);
    },

    /**
     * Removes all structured data script tags from the page
     */
    removeAllStructuredData() {
      const scripts = document.querySelectorAll('script[type="application/ld+json"][data-schema-type]');
      scripts.forEach(script => script.remove());
    },

    /**
     * Generates Organization schema (use globally in App.vue or main layout)
     * @returns {Object} Organization schema
     */
    getOrganizationSchema() {
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Kootto",
        "url": "https://kootto.com",
        "logo": "https://kootto.com/img/icons/logo-text-background-banner.png",
        "description": "Private chef and catering service in Australia",
        "taxID": "88 642 355 708",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "AU"
        },
        "sameAs": [
          "https://www.facebook.com/koottoprivatechefs",
          "https://www.instagram.com/kootto_privatechef/",
          "https://www.linkedin.com/company/kootto"
        ]
      };
    },

    /**
     * Generates LocalBusiness schema for city-specific pages
     * @param {String} cityName - The city name (e.g., "Gold Coast", "Sydney")
     * @param {Object} options - Optional parameters (rating, reviewCount, priceRange, telephone, etc.)
     *                           These values should come from city JSON files for dynamic city-specific data
     * @returns {Object} LocalBusiness schema
     */
    getLocalBusinessSchema(cityName, options = {}) {
      const {
        rating = "4.9",
        reviewCount = "150",
        priceRange = "$$-$$$",
        region = "",
        address = {},
        url = ""
      } = options;

      return {
        "@context": "https://schema.org",
        "@type": "FoodEstablishment",
        "name": `Kootto - Private Chef ${cityName}`,
        "image": "https://kootto.com/img/home_1920.webp",
        "url": url || `https://kootto.com/private-chef/${cityName.replace(/ /g, '-')}`,
        "priceRange": priceRange,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": cityName,
          "addressRegion": region,
          "addressCountry": "AU",
          ...address
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": rating,
          "reviewCount": reviewCount
        },
        "servesCuisine": [
          "Modern Australian",
          "Italian",
          "Japanese",
          "Mediterranean",
          "Asian Fusion",
          "French"
        ],
        "areaServed": {
          "@type": "City",
          "name": cityName
        }
      };
    },

    /**
     * Generates Service schema
     * @param {String} cityName - The city name (optional)
     * @returns {Object} Service schema
     */
    getServiceSchema(cityName = "Australia") {
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Private Chef Service",
        "provider": {
          "@type": "Organization",
          "name": "Kootto"
        },
        "areaServed": {
          "@type": cityName === "Australia" ? "Country" : "City",
          "name": cityName
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Private Chef Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Course Meals",
                "description": "Elegant multi-course sit-down dining experiences prepared by professional chefs"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Cocktail Party & Finger Food",
                "description": "Sophisticated cocktail party catering with gourmet canapés and finger food"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Sharing Platters",
                "description": "Family-style sharing platters perfect for casual gatherings and group dining"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Grazing Tables & Buffets",
                "description": "Impressive grazing tables and buffet-style catering for events and celebrations"
              }
            }
          ]
        }
      };
    },

    /**
     * Generates FAQPage schema from FAQ items
     * @param {Array} faqItems - Array of FAQ objects with 'question' and 'answer' properties
     * @param {String} pageUrl - Canonical page URL used to generate stable @id values
     * @returns {Object} FAQPage schema
     */
    getFAQSchema(faqItems, pageUrl = "") {
      if (!faqItems || !Array.isArray(faqItems) || faqItems.length === 0) {
        return null;
      }

      const sanitize = (value = "") => String(value).replace(/\s+/g, ' ').trim();
      const validFaqItems = faqItems
        .map(item => ({
          question: sanitize(item?.question),
          answer: sanitize(item?.answer)
        }))
        .filter(item => item.question && item.answer);

      if (validFaqItems.length === 0) {
        return null;
      }

      const faqId = pageUrl ? `${pageUrl}#faq` : undefined;

      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        ...(faqId ? { "@id": faqId } : {}),
        "mainEntity": validFaqItems.map((item, index) => ({
          "@type": "Question",
          ...(faqId ? { "@id": `${faqId}-q${index + 1}` } : {}),
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      };
    },

    /**
     * Generates BreadcrumbList schema
     * @param {Array} breadcrumbs - Array of breadcrumb objects with 'name' and 'url' properties
     * @returns {Object} BreadcrumbList schema
     */
    getBreadcrumbSchema(breadcrumbs) {
      if (!breadcrumbs || !Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        return null;
      }

      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.url
        }))
      };
    },

    /**
     * Generates a Product/Service schema with embedded individual Reviews and AggregateRating
     * Suitable for Google rich results (star ratings in SERPs)
     * @param {String} serviceName - The name of the service being reviewed
     * @param {String} serviceUrl - The canonical URL of the service page
     * @param {Array} reviews - Array of review objects with { name, location, rating, text }
     * @param {Object} aggregateOptions - { ratingValue, reviewCount }
     * @returns {Object} Product schema with Review and AggregateRating
     */
    /**
     * Generates BlogPosting schema
     * @param {Object} article - Article details { title, description, datePublished, dateModified, author, image, url }
     * @returns {Object} BlogPosting schema
     */
    getBlogArticleSchema(article) {
      const {
        title,
        description,
        datePublished,
        dateModified,
        author = "Kootto",
        image = "https://kootto.com/img/home_1920.webp",
        url
      } = article;

      return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": title,
        "description": description,
        "image": image,
        "author": {
          "@type": author.includes("&") ? "Organization" : "Person",
          "name": author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Kootto",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kootto.com/img/icons/logo-text-background-banner.png"
          }
        },
        "datePublished": datePublished,
        "dateModified": dateModified || datePublished,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": url
        }
      };
    },

    /**
     * Generates ItemList schema for collections (Blog index, search results, etc.)
     * @param {String} name - List name
     * @param {Array} items - List elements { name, url }
     * @returns {Object} ItemList schema
     */
    getItemListSchema(name, items) {
      return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": name,
        "itemListElement": items.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "url": item.url
        }))
      };
    },

    /**
     * Generates VideoObject schema
     * @param {Object} video - Video details { name, description, thumbnailUrl, uploadDate, contentUrl, embedUrl, duration }
     * @returns {Object} VideoObject schema
     */
    getVideoSchema(video) {
      const {
        name,
        description,
        thumbnailUrl,
        uploadDate,
        contentUrl,
        embedUrl,
        duration,
        publisher = "Kootto"
      } = video;

      return {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": name,
        "description": description,
        "thumbnailUrl": thumbnailUrl,
        "uploadDate": uploadDate,
        "contentUrl": contentUrl,
        "embedUrl": embedUrl || contentUrl,
        "duration": duration, // ISO 8601 duration format (e.g., PT1M30S)
        "publisher": {
          "@type": "Organization",
          "name": publisher,
          "logo": {
            "@type": "ImageObject",
            "url": "https://kootto.com/img/icons/logo-text-background-banner.png"
          }
        }
      };
    },

    getReviewSchema(serviceName, serviceUrl, reviews = [], aggregateOptions = {}) {
      const {
        ratingValue = "4.9",
        reviewCount = "150"
      } = aggregateOptions;

      return {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": serviceName,
        "url": serviceUrl,
        "brand": {
          "@type": "Brand",
          "name": "Kootto"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": ratingValue,
          "bestRating": "5",
          "worstRating": "1",
          "reviewCount": reviewCount
        },
        "review": reviews.map(review => ({
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": review.name
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": String(review.rating),
            "bestRating": "5",
            "worstRating": "1"
          },
          "reviewBody": review.text,
          "locationCreated": {
            "@type": "Place",
            "name": review.location + ", Australia"
          }
        }))
      };
    },

    /**
      const {
        itemName = "Kootto Private Chef Service",
        ratingValue = "4.9",
        bestRating = "5",
        ratingCount = "150"
      } = options;

      return {
        "@type": "AggregateRating",
        "itemReviewed": {
          "@type": "Service",
          "name": itemName
        },
        "ratingValue": ratingValue,
        "bestRating": bestRating,
        "ratingCount": ratingCount
      };
      }
     **/

    beforeDestroy() {
      // Clean up structured data when component is destroyed
      this.removeAllStructuredData();
    }
  }
}
